import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Star } from 'lucide-react';
import { testimonials } from '../data/salesContent';
import { SectionShell } from './SectionShell';
import { TestimonialCard } from './TestimonialCard';
import type { Testimonial } from '../types/sales';
const edgeMask = 'linear-gradient(to right, transparent 0%, #000 7%, #000 93%, transparent 100%)';
interface MarqueeRowProps {
  items: Testimonial[];
  duration: number;
  reverse?: boolean;
  label: string;
}
function MarqueeRow({
  items,
  duration,
  reverse = false,
  label
}: MarqueeRowProps) {
  const ref = useRef<HTMLDivElement>(null);
  // Only animate while the row is actually on screen (saves mobile CPU).
  const inView = useInView(ref, {
    amount: 0.1
  });
  return <div ref={ref} className="group relative overflow-hidden py-1" style={{
    maskImage: edgeMask,
    WebkitMaskImage: edgeMask
  }} data-id="element-268" data-mp="2yf7j62281uq5">
      <ul aria-label={label} className="flex w-max animate-marquee items-stretch group-hover:[animation-play-state:paused]" style={{
      animationDuration: `${duration}s`,
      animationDirection: reverse ? 'reverse' : 'normal',
      animationPlayState: inView ? 'running' : 'paused'
    }} data-id="element-269" data-mp="32fhordm3qj4h">
        {[0, 1].map(copy => items.map(testimonial => <li key={`${copy}-${testimonial.name}`} aria-hidden={copy === 1} className="mr-3.5 w-[275px] shrink-0 sm:mr-5 sm:w-[350px]" data-id="element-270" data-mp="2zst17k8ozv8x">
              <TestimonialCard testimonial={testimonial} data-id="element-271" data-mp="2756n3i1u987t" />
            </li>))}
      </ul>
    </div>;
}
export function TestimonialsSection() {
  const half = Math.ceil(testimonials.length / 2);
  const rowOne = testimonials.slice(0, half);
  const rowTwo = testimonials.slice(half);
  return <SectionShell eyebrow="Testimonios" title="Lo que dicen quienes ya lo leyeron" subtitle={`${testimonials.length} lectores reales, cinco países distintos, el mismo patrón: dejaron de adivinar.`} className="bg-noir-950" data-id="element-272" data-mp="18u8eexfz51i">
      <motion.div initial={{
      opacity: 0,
      y: 26
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-70px'
    }} transition={{
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }} className="-mx-[1.15rem] space-y-3.5 sm:mx-0 sm:space-y-5" data-id="element-273" data-mp="8dc3xu3xs5dn">
        <MarqueeRow items={rowOne} duration={62} label="Testimonios, primera fila" data-id="element-274" data-mp="3gu6z0nehslh6" />
        <MarqueeRow items={rowTwo} duration={74} reverse label="Testimonios, segunda fila" data-id="element-275" data-mp="3lsrq9xbrnuth" />
      </motion.div>

      <div className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] text-cream/45 sm:mt-12 sm:gap-x-6 sm:gap-y-3 sm:text-xs" data-id="element-276" data-mp="3qqa2bwl2yq5p">
        <span className="flex items-center gap-2" data-id="element-277" data-mp="2y3payw4lue25">
          <span className="flex gap-0.5" aria-hidden="true" data-id="element-278" data-mp="2y2xfdlb0cgbv">
            {Array.from({
            length: 5
          }).map((_, index) => <Star key={index} className="h-3.5 w-3.5 fill-gold-400 text-gold-400" data-id="element-279" data-mp="1k75r4v1minvj" data-mpi={index} />)}
          </span>
          <span className="font-semibold text-cream/75" data-id="element-280" data-mp="1g5u1ipir22nc">4.9 / 5</span> promedio de
          valoración
        </span>
        <span aria-hidden="true" className="hidden h-3 w-px bg-cream/15 sm:block" data-id="element-281" data-mp="1b5oejmnfd4bl" />
        <span data-id="element-282" data-mp="167waxvdf3trk">Pasa el cursor para pausar el carrusel</span>
      </div>
    </SectionShell>;
}